import { useIsHeidi2OptInEnabled } from '@/features/flags';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import {
  trackComplimentaryTrialEndedModalClick,
  trackComplimentaryTrialEndedModalViewed,
} from '@/models/analytics/features/complimentary-trial-end-analytics';
import {
  ComplimentaryTrialEndedModal,
  type ComplimentaryTrialEndedModalProps,
  useComplimentaryTrialEndedPlanNames,
  useComplimentaryTrialEndedUsagePercent,
} from '@heidi/commerce';
import type { ComplimentaryTrialEndedModalAction } from '@heidi/commerce/contracts';
import {
  COMPLIMENTARY_TRIAL_ENDED_MODAL_ID,
  useUserBillingInfo,
} from '@heidi/commerce/data';
import { ModalViewAction } from '@heidi/schema/ml-scribe';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useOpenClinicianCheckout } from './clinician-upgrade-checkout-route';

type UsageClass = ComplimentaryTrialEndedModalProps['usageClass'];

const OpenComplimentaryTrialEndedModal = ({ usageClass }: { usageClass: UsageClass }) => {
  const isHeidi2OptInEnabled = useIsHeidi2OptInEnabled();
  const { closeWithOptions } = useQueuedModal(COMPLIMENTARY_TRIAL_ENDED_MODAL_ID);
  const planNames = useComplimentaryTrialEndedPlanNames({ isHeidi2OptInEnabled });
  const openClinicianCheckout = useOpenClinicianCheckout();
  const { data: billingInfo } = useUserBillingInfo();
  const usagePercent = useComplimentaryTrialEndedUsagePercent();
  const currentPlan = billingInfo?.new_product_tier ?? null;
  const analyticsContext = useMemo(
    () => ({ currentPlan, usagePercent }),
    [currentPlan, usagePercent]
  );

  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (viewTrackedRef.current || planNames == null) return;
    viewTrackedRef.current = true;
    trackComplimentaryTrialEndedModalViewed(analyticsContext);
  }, [analyticsContext, planNames]);

  const trackClick = useCallback(
    (action: ComplimentaryTrialEndedModalAction, label: string) =>
      trackComplimentaryTrialEndedModalClick({ ...analyticsContext, action, label }),
    [analyticsContext]
  );

  const handleUpgrade = useCallback(() => {
    trackClick('upgrade', `Upgrade to ${planNames?.targetPlanName ?? ''}`.trim());
    closeWithOptions({ action: ModalViewAction.engage });
    openClinicianCheckout();
  }, [closeWithOptions, openClinicianCheckout, planNames?.targetPlanName, trackClick]);

  const handleStay = useCallback(() => {
    trackClick('stay', `Stay on ${planNames?.currentPlanName ?? ''}`.trim());
    closeWithOptions({ action: ModalViewAction.dismiss });
  }, [closeWithOptions, planNames?.currentPlanName, trackClick]);

  // The dialog only reports user dismissals (×, overlay, Escape, drag).
  const handleDismiss = useCallback(() => {
    trackClick('close', 'close');
    closeWithOptions({ action: ModalViewAction.dismiss });
  }, [closeWithOptions, trackClick]);

  if (planNames == null) return null;

  return (
    <ComplimentaryTrialEndedModal
      open
      usageClass={usageClass}
      currentPlanName={planNames.currentPlanName}
      targetPlanName={planNames.targetPlanName}
      onUpgrade={handleUpgrade}
      onStay={handleStay}
      onDismiss={handleDismiss}
    />
  );
};

export const ComplimentaryTrialEndedModalHost = ({
  usageClass,
}: {
  usageClass: UsageClass | null;
}) => {
  const { isOpen } = useQueuedModal(COMPLIMENTARY_TRIAL_ENDED_MODAL_ID);

  if (!isOpen || usageClass == null) return null;

  return <OpenComplimentaryTrialEndedModal usageClass={usageClass} />;
};
